import CheckBoxIcon from '@mui/icons-material/CheckBox'
import CorporateFareIcon from '@mui/icons-material/CorporateFare'
import { Box, Tooltip, Typography } from '@mui/material'
import { GridRenderCellParams } from '@mui/x-data-grid-premium'
import { renderChannelPlatformIcons } from 'components/TeamTasks/renderChannelIcons'
import RenderOptionIcon from 'components/TeamTasks/renderOptionIcon'
import React from 'react'
import { DensityStyleTypes, Task } from 'types'

const Name_Cell = ({
  params,
  onTaskClick,
  densityStyles,
}: {
  params: GridRenderCellParams
  onTaskClick: (_: any, taskData: Task) => void
  densityStyles: DensityStyleTypes
}) => {
  const { row } = params ?? {}
  const isDigitalCommandCenter = row?.isCommandCenter && (row?.isMarketing || row?.channel === 'Digital')
  const isCreativeCommandCenter = row?.isCommandCenter && (row?.isCreative || row?.channel === 'Creative')
  const orgScope = row?.scope === 'organization'
  const platformIcons = renderChannelPlatformIcons(row)
  const badgeColor = isDigitalCommandCenter ? 'icon.nco' : 'icon.projects'
  const splitName = row?.name?.split('-')
  const displayName = splitName?.length > 1 ? splitName[splitName.length - 1].trim() : params?.value
  const displayBadge = (
    <Tooltip title={isDigitalCommandCenter ? 'NCO' : 'Projects'}>
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: -12,
          height: 6,
          width: 6,
          borderRadius: '50%',
          bgcolor: badgeColor,
        }}
      />
    </Tooltip>
  )

  return (
    <Box
      className="task-name-cell"
      sx={{
        display: 'flex',
        alignItems: 'center', // ✅ ensures vertical centering
        width: '100%',
        pl: `${2}px`,
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: 14,
        color: 'inherit',
        height: '100%', // ✅ optional but enforces height awareness
        cursor: 'pointer',
      }}
      onClick={event => onTaskClick(event, row)}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'left',
          width: '100%',
          maxWidth: '100%',
          minWidth: 0, // Allows flex children to shrink below content size
          boxSizing: 'border-box',
          padding: densityStyles.padding,
          pl: 1.5,
          gap: densityStyles.gap,
          cursor: 'pointer',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: 'inline-block',
            minWidth: 0, // Allows text to truncate properly
            flex: '1 1 auto', // Grow and shrink as needed
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {(isDigitalCommandCenter || isCreativeCommandCenter) && displayBadge}
          <Typography
            variant="body2"
            sx={{ fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {displayName}
          </Typography>
        </Box>

        {/* Channel/Platform icons */}
        {platformIcons.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 0.5, pr: 1.5, flexShrink: 0 }}>
            {platformIcons.map((item, index) => {
              return (
                <React.Fragment key={index}>
                  {typeof item?.icon === 'string' ? (
                    <Tooltip title={item.label}>
                      <img
                        src={item.icon}
                        alt={item.label}
                        style={{
                          width: densityStyles.iconSize,
                          height: densityStyles.iconSize,
                          objectFit: 'contain',
                        }}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title={item.label}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          height: densityStyles.iconSize,
                          width: densityStyles.iconSize,
                        }}
                      >
                        <RenderOptionIcon icon={item.icon} size={densityStyles.iconSize} />
                      </Box>
                    </Tooltip>
                  )}
                  {row?.taskType?.includes('campaignChecklists') && row?.checklist?.length > 0 && (
                    <Tooltip title={<Typography>{`${row?.progress?.done} / ${row?.progress?.total}`}</Typography>}>
                      <CheckBoxIcon />
                    </Tooltip>
                  )}
                </React.Fragment>
              )
            })}
          </Box>
        )}
        {orgScope && (
          <CorporateFareIcon
            sx={{
              // color: 'text.disabled',
              color: 'icon.organizations',
            }}
          />
        )}
      </Box>
    </Box>
  )
}

export default Name_Cell
